'use client';

import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import { TIME_LEFT_WARNING_THRESHOLD_IN_DAYS } from '@/app/constants';

interface ProjectTimeLeftProps extends PropsWithClassname {
  endsAt: Date;
}

const ProjectTimeLeft = ({ endsAt, className }: ProjectTimeLeftProps) => {
  const { t } = useTranslation();

  const daysRemaining = differenceInDays(endsAt, new Date());

  const timeRemainingText = useMemo(() => {
    const hoursRemaining = differenceInHours(endsAt, new Date());
    const minutesRemaining = differenceInMinutes(endsAt, new Date());

    if (minutesRemaining < 60) {
      return t('components.ProjectTimeLeft.minutesRemaining', { count: minutesRemaining });
    }

    if (hoursRemaining < 24) {
      return t('components.ProjectTimeLeft.hoursRemaining', { count: hoursRemaining });
    }

    return t('components.ProjectTimeLeft.daysRemaining', { count: daysRemaining });
  }, [t, endsAt, daysRemaining]);

  return (
    <div className={classNames(className)}>
      <span
        className={classNames(
          'inline-block text-[20px] whitespace-nowrap tabular-nums',
          daysRemaining < TIME_LEFT_WARNING_THRESHOLD_IN_DAYS && 'text-warning  font-medium',
        )}
      >
        {timeRemainingText}
      </span>
      <span className="block text-[14px] text-neutral-semiTransparent">
        {t('components.ProjectTimeLeft.tillCompletion')}
      </span>
    </div>
  );
};

export default ProjectTimeLeft;
